/**
 * The time zone check for a location's zone, shared by the console's create-location request and the clinic's
 * location settings. A location's zone is used in SQL (`at time zone`, e.g. `packages/database/src/reps/volume.ts`
 * and `billing/supply-balance.ts`) as well as in the browser, so a name must satisfy both. Browser-safe: no
 * implementation beyond `Intl`.
 */

/**
 * Names ICU still resolves that the tz database has removed (Canada/East-Saskatchewan in 2017c, US/Pacific-New and
 * the SystemV links in 2020b). PostgreSQL refuses the first two in `at time zone` and lists none of them in
 * `pg_timezone_names`, so a location saved with one would break every query that converts its wall times.
 */
const REMOVED_FROM_TZDATA = /^(?:Canada\/East-Saskatchewan|US\/Pacific-New|SystemV\/.+)$/i;

/**
 * An IANA zone name of the Area/Location form (`America/Denver`) that this runtime knows and that the tz database
 * still carries. Offsets (`+01:00`) and abbreviations (`MST`) are refused: PostgreSQL reads a bare offset with the
 * opposite sign. Not `Intl.supportedValuesOf("timeZone")`: Node's ICU lists CLDR's spellings there (`Asia/Calcutta`,
 * `America/Indianapolis`) and leaves out current IANA names (`Asia/Kolkata`, `America/Indiana/Indianapolis`), and
 * each browser's list differs. The database store also checks the cluster's own list before it writes a zone
 * (`services/api/src/lib/time-zone.ts`), because an Aurora engine's tzdata can differ from Node's ICU.
 */
export function isIanaTimeZone(zone: string): boolean {
  if (!/^[A-Za-z]+(?:\/[A-Za-z0-9_+-]+)+$/.test(zone) || REMOVED_FROM_TZDATA.test(zone)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}
